<template>
  <b-card>
    <b-card-title>
      <h3>{{ isEditMode ? "Edit Announcement" : "Announcement Details" }}</h3>
    </b-card-title>
    <base-overlay :show="loading">
      <validation-observer ref="createRef">
        <b-form @submit.prevent="submit">
          <b-row>
            <!-- Zones Multi-Select -->
            <b-col md="12">
              <validation-provider
                #default="{ errors }"
                name="Zones"
                rules="required"
              >
                <ZonesMultiSelect
                  v-model="formData.zones"
                  :disabled="isEditMode"
                />
                <span class="text-danger"> {{ errors[0] }} </span>
              </validation-provider>
            </b-col>

            <!-- Publish Option -->
            <b-col md="12">
              <validation-provider
                #default="{ errors }"
                name="Publish Option"
                rules="required"
              >
                <b-form-group label="Publish Option" label-for="publish-option">
                  <v-select
                    id="publish-option"
                    v-model="formData.publish_option"
                    :reduce="(val) => val.value"
                    :options="PUBLISH_OPTIONS"
                    :disabled="isEditMode"
                  />
                  <span class="text-danger"> {{ errors[0] }} </span>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Announcement Text -->
            <b-col md="6">
              <validation-provider
                #default="{ errors }"
                name="Announcement en"
                rules="required"
              >
                <b-form-group
                  label="Announcement en"
                  label-for="Announcement-en"
                >
                  <b-form-textarea
                    id="Announcement-en"
                    v-model="formData.announcement.en"
                  />
                  <span class="text-danger"> {{ errors[0] }} </span>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col md="6">
              <validation-provider
                #default="{ errors }"
                name="Announcement ar"
                rules="required"
              >
                <b-form-group
                  label="Announcement ar"
                  label-for="Announcement-ar"
                >
                  <b-form-textarea
                    id="Announcement-ar"
                    v-model="formData.announcement.ar"
                  />
                  <span class="text-danger"> {{ errors[0] }} </span>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Start Date & Time (for scheduled and recurring) -->
            <b-col
              v-if="
                formData.publish_option === 'scheduled' ||
                  formData.publish_option === 'recurring'
              "
              md="6"
            >
              <b-form-group label="Start Date & Time" />
              <b-form-datepicker
                v-model="startDate"
                class="mb-1"
                :min="new Date()"
              />
              <b-form-timepicker v-model="startTime" />
            </b-col>

            <!-- End Date & Time -->
            <b-col md="6">
              <div class="mb-1">
                <div class="d-flex justify-content-between align-items-center">
                  <span>End Date & Time</span>
                  <b-button
                    v-if="formData.publish_option === 'recurring'"
                    variant="outline-secondary"
                    size="sm"
                    @click="clearEndDateTime"
                  >
                    Clear
                  </b-button>
                </div>
              </div>
              <b-form-datepicker
                v-model="endDate"
                class="mb-1"
                :min="new Date()"
              />
              <b-form-timepicker v-model="endTime" />
            </b-col>

            <!-- Recurring Options -->
            <template v-if="formData.publish_option === 'recurring'">
              <b-col md="6" class="mt-3">
                <validation-provider
                  #default="{ errors }"
                  name="Frequency"
                  rules="required"
                >
                  <b-form-group label="Frequency" label-for="frequency">
                    <v-select
                      id="frequency"
                      v-model="formData.recurring.frequency"
                      :reduce="(val) => val.value"
                      :options="FREQUENCY_OPTIONS"
                    />
                    <span class="text-danger"> {{ errors[0] }} </span>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col md="3" class="mt-3">
                <b-form-group
                  label="Start Time"
                  label-for="recurring-start-time"
                >
                  <b-form-timepicker
                    id="recurring-start-time"
                    v-model="formData.recurring.start_time"
                  />
                </b-form-group>
              </b-col>

              <b-col md="3" class="mt-3">
                <b-form-group label="End Time" label-for="recurring-end-time">
                  <b-form-timepicker
                    id="recurring-end-time"
                    v-model="formData.recurring.end_time"
                  />
                </b-form-group>
              </b-col>

              <b-col
                v-if="
                  formData.recurring.frequency === 'weekly' ||
                    formData.recurring.frequency === 'daily'
                "
                md="12"
              >
                <b-form-group label="Days of Week" label-for="days-of-week">
                  <v-select
                    id="days-of-week"
                    v-model="formData.recurring.days_of_week"
                    :reduce="(val) => val.value"
                    :options="DAYS_OF_WEEK"
                    multiple
                  />
                </b-form-group>
              </b-col>
            </template>

            <!-- Appearance Options -->
            <b-col md="4" class="mt-1">
              <b-form-group label="Text Color">
                <BFormInput value="White" readonly />
              </b-form-group>
            </b-col>
            <b-col md="4" class="mt-1">
              <b-form-group label="Background Color">
                <v-select
                  v-model="formData.announcement.backgroundColor"
                  :reduce="(val) => val.value"
                  :options="BACKGROUND_COLORS"
                />
              </b-form-group>
            </b-col>
            <b-col md="4" class="mt-1">
              <b-form-group label="Enabled">
                <BFormCheckbox v-model="formData.announcement.enabled" switch />
              </b-form-group>
            </b-col>

            <!-- Submit Button -->
            <b-col md="6">
              <b-button type="submit" variant="primary" :disabled="submitting">
                <b-spinner v-if="submitting" small />
                <span v-else>{{ isEditMode ? "Update" : "Save" }}</span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </base-overlay>
  </b-card>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import {
  BFormGroup,
  BForm,
  BFormTextarea,
  BCard,
  BRow,
  BCol,
  BButton,
  BCardTitle,
  BSpinner,
  BFormDatepicker,
  BFormTimepicker,
  BFormInput,
  BFormCheckbox,
} from "bootstrap-vue";
import { required } from "@validations";
import vSelect from "vue-select";
import { ref, onMounted } from "@vue/composition-api";
import {
  errorNotification,
  successNotification,
} from "@/utils/toast-notifications";
import useRegisterStoreModule from "@/hooks/useRegisterStoreModule";
import store from "../store";
import ZonesMultiSelect from "./ZonesMultiSelect.vue";
import BaseOverlay from "@/UI/BaseOverlay.vue";

export default {
  components: {
    BForm,
    vSelect,
    BFormGroup,
    BFormCheckbox,
    BSpinner,
    BFormInput,
    BFormTextarea,
    BButton,
    BCard,
    ValidationObserver,
    ValidationProvider,
    BRow,
    BCol,
    BCardTitle,
    BFormDatepicker,
    BFormTimepicker,
    ZonesMultiSelect,
    BaseOverlay,
  },

  setup(_, ctx) {
    useRegisterStoreModule("announcement", store);

    // Check if we're in edit mode
    const isEditMode = ref(false);
    const announcementId = ref(null);

    // Check route params for edit mode
    if (ctx.root.$route.params.id) {
      isEditMode.value = true;
      announcementId.value = ctx.root.$route.params.id;
    }
    const BACKGROUND_COLORS = [
      {
        label: "Red",
        value: "#FF0F0F",
      },
      {
        label: "Orange",
        value: "#DE7400",
      },
      {
        label: "Green",
        value: "#21A600",
      },
      {
        label: "Blue",
        value: "#043CA1",
      },
    ];

    const PUBLISH_OPTIONS = [
      {
        label: "Immediate",
        value: "immediate",
      },
      {
        label: "Scheduled",
        value: "scheduled",
      },
      {
        label: "Recurring",
        value: "recurring",
      },
    ];

    const FREQUENCY_OPTIONS = [
      {
        label: "Daily",
        value: "daily",
      },
      {
        label: "Weekly",
        value: "weekly",
      },
      {
        label: "Monthly",
        value: "monthly",
      },
      {
        label: "Annually",
        value: "annually",
      },
    ];

    const DAYS_OF_WEEK = [
      {
        label: "Monday",
        value: "monday",
      },
      {
        label: "Tuesday",
        value: "tuesday",
      },
      {
        label: "Wednesday",
        value: "wednesday",
      },
      {
        label: "Thursday",
        value: "thursday",
      },
      {
        label: "Friday",
        value: "friday",
      },
      {
        label: "Saturday",
        value: "saturday",
      },
      {
        label: "Sunday",
        value: "sunday",
      },
    ];

    const startTime = ref();
    const startDate = ref();
    const endTime = ref();
    const endDate = ref();

    const formData = ref({
      zones: [],
      announcement: {
        ar: null,
        en: null,
        backgroundColor: "#FF0F0F",
        textColor: "#FFFFFF",
        enabled: true,
      },
      publish_option: "immediate",
      start_date_time: null,
      end_date_time: null,
      country: "JO",
      recurring: {
        frequency: "weekly",
        start_time: "09:00",
        end_time: "17:00",
        days_of_week: [],
      },
    });

    // UTC time conversion utilities
    // When sending to backend: convert local time to UTC
    // When receiving from backend: convert UTC to local time
    const convertToUTC = (localTime) => {
      if (!localTime) return localTime;
      const [hours, minutes] = localTime.split(":").map(Number);
      const localDate = new Date();
      localDate.setHours(hours, minutes, 0, 0);

      // Convert to UTC using proper timezone handling
      const utcHours = localDate
        .getUTCHours()
        .toString()
        .padStart(2, "0");
      const utcMinutes = localDate
        .getUTCMinutes()
        .toString()
        .padStart(2, "0");
      return `${utcHours}:${utcMinutes}`;
    };

    const convertFromUTC = (utcTime) => {
      if (!utcTime) return utcTime;
      const [hours, minutes] = utcTime.split(":").map(Number);
      const utcDate = new Date();
      utcDate.setUTCHours(hours, minutes, 0, 0);

      // Convert from UTC to local timezone
      const localHours = utcDate
        .getHours()
        .toString()
        .padStart(2, "0");
      const localMinutes = utcDate
        .getMinutes()
        .toString()
        .padStart(2, "0");
      return `${localHours}:${localMinutes}`;
    };

    const setAnnouncementData = () => {
      startTime.value = "12:00:00";
      endTime.value = "12:00:00";
      const [date] = new Date().toISOString().split("T");
      startDate.value = date;
      endDate.value = date;
      formData.value.zones = [];
      formData.value.announcement.en = null;
      formData.value.announcement.ar = null;
      formData.value.announcement.enabled = true;
      formData.value.publish_option = "immediate";
      formData.value.recurring.frequency = "weekly";
      formData.value.recurring.start_time = "09:00";
      formData.value.recurring.end_time = "17:00";
      formData.value.recurring.days_of_week = [];
    };
    setAnnouncementData();
    const submitting = ref(false);
    const loading = ref(false);

    // Function to load announcement data for editing
    const loadAnnouncementData = async () => {
      if (!isEditMode.value || !announcementId.value) return;

      try {
        loading.value = true;
        const response = await ctx.root.$store.dispatch(
          "announcement/getAnnouncement",
          announcementId.value
        );
        const announcementData = response.data.data;

        // Populate form with existing data
        formData.value.zones = [announcementData.zone] || [];
        formData.value.announcement.ar =
          announcementData.announcement?.ar || "";
        formData.value.announcement.en =
          announcementData.announcement?.en || "";
        formData.value.announcement.backgroundColor =
          announcementData.announcement?.backgroundColor || "#FF0F0F";
        formData.value.announcement.textColor =
          announcementData.announcement?.textColor || "#FFFFFF";
        formData.value.announcement.enabled =
          announcementData.announcement?.enabled || false;
        formData.value.publish_option =
          announcementData.publishOption || "immediate";

        // Handle dates and times
        if (announcementData.startDateTime) {
          const startDateTime = new Date(announcementData.startDateTime);
          const [dateStr] = startDateTime.toISOString().split("T");
          const [timeStr] = startDateTime.toTimeString().split(" ");
          startDate.value = dateStr;
          startTime.value = timeStr;
        }

        if (announcementData.endDateTime) {
          const endDateTime = new Date(announcementData.endDateTime);
          const [dateStr] = endDateTime.toISOString().split("T");
          const [timeStr] = endDateTime.toTimeString().split(" ");
          endDate.value = dateStr;
          endTime.value = timeStr;
        }

        // Handle recurring data
        if (announcementData.recurring) {
          formData.value.recurring = {
            frequency: announcementData.recurring.frequency || "weekly",
            start_time:
              convertFromUTC(announcementData.recurring.start_time) || "09:00",
            end_time:
              convertFromUTC(announcementData.recurring.end_time) || "17:00",
            days_of_week: announcementData.recurring.days_of_week || [],
          };
        }
      } catch (error) {
        console.error("Error loading announcement:", error);
        errorNotification("Failed to load announcement data");
      } finally {
        loading.value = false;
      }
    };

    // Load data on mount if in edit mode
    onMounted(() => {
      if (isEditMode.value) {
        loadAnnouncementData();
      }
    });

    const clearEndDateTime = () => {
      endDate.value = null;
      endTime.value = null;
    };

    const submit = async () => {
      const res = await ctx.refs.createRef.validate();
      if (!res) return;

      // Validate zones
      if (!formData.value.zones || formData.value.zones.length === 0) {
        errorNotification("Please select at least one zone");
        return;
      }

      const basePayload = {
        zones: formData.value.zones,
        announcement: formData.value.announcement,
        publish_option: formData.value.publish_option,
        country: "JO",
      };

      // For edit mode, create payload without publish_option
      const updateBasePayload = {
        zones: formData.value.zones,
        announcement: formData.value.announcement,
        country: "JO",
      };

      // Choose the appropriate base payload based on mode
      const currentBasePayload = isEditMode.value
        ? updateBasePayload
        : basePayload;
      let payload = currentBasePayload;

      // Handle different publish options
      if (formData.value.publish_option === "immediate") {
        const endDateTimeTemp = new Date(`${endDate.value} ${endTime.value}`);
        payload = {
          ...currentBasePayload,
          end_date_time: endDateTimeTemp.toISOString(),
        };
      } else if (formData.value.publish_option === "scheduled") {
        const startDateTimeTemp = new Date(
          `${startDate.value} ${startTime.value}`
        );
        const endDateTimeTemp = new Date(`${endDate.value} ${endTime.value}`);

        if (startDateTimeTemp.getTime() >= endDateTimeTemp.getTime()) {
          errorNotification(
            "Start date & time cannot be greater or equal to End date & time"
          );
          return;
        }

        payload = {
          ...currentBasePayload,
          start_date_time: startDateTimeTemp.toISOString(),
          end_date_time: endDateTimeTemp.toISOString(),
        };
      } else if (formData.value.publish_option === "recurring") {
        const startDateTimeTemp = new Date(
          `${startDate.value} ${startTime.value}`
        );
        let endDateTimeTemp = null;

        // Only validate end date if it's provided
        if (endDate.value && endTime.value) {
          endDateTimeTemp = new Date(`${endDate.value} ${endTime.value}`);

          if (startDateTimeTemp.getTime() >= endDateTimeTemp.getTime()) {
            errorNotification(
              "Start date & time cannot be greater or equal to End date & time"
            );
            return;
          }
        }

        // Validate recurring fields
        if (!formData.value.recurring.frequency) {
          errorNotification(
            "Please select a frequency for recurring announcement"
          );
          return;
        }

        if (
          (formData.value.recurring.frequency === "weekly" ||
            formData.value.recurring.frequency === "daily") &&
          (!formData.value.recurring.days_of_week ||
            formData.value.recurring.days_of_week.length === 0)
        ) {
          errorNotification(
            "Please select days of week for recurring announcement"
          );
          return;
        }

        // Format times to HH:MM format (remove seconds if present)
        const formatTimeToHHMM = (time) => {
          if (!time) return time;
          return time.substring(0, 5); // Extract HH:MM from HH:MM:SS
        };

        const recurringData = {
          frequency: formData.value.recurring.frequency,
          start_time: convertToUTC(
            formatTimeToHHMM(formData.value.recurring.start_time)
          ),
          end_time: convertToUTC(
            formatTimeToHHMM(formData.value.recurring.end_time)
          ),
        };

        // Add days_of_week for weekly/daily frequency
        if (
          formData.value.recurring.frequency === "weekly" ||
          formData.value.recurring.frequency === "daily"
        ) {
          recurringData.days_of_week = formData.value.recurring.days_of_week;
        }

        payload = {
          ...currentBasePayload,
          start_date_time: startDateTimeTemp?.toISOString() || undefined,
          end_date_time: endDateTimeTemp?.toISOString() || undefined,
          recurring: recurringData,
        };

        // Remove end_date_time from payload if it's null/undefined
        if (!endDateTimeTemp) {
          delete payload.end_date_time;
        }
      }

      try {
        submitting.value = true;

        if (isEditMode.value) {
          // Handle update logic
          if (formData.value.publish_option === "recurring") {
            // Update recurring announcement separately
            const recurringPayload = {
              recurring: payload.recurring,
            };
            await ctx.root.$store.dispatch(
              "announcement/updateRecurringAnnouncement",
              {
                id: announcementId.value,
                payload: recurringPayload,
              }
            );

            // Update main announcement data (excluding recurring)
            const mainPayload = { ...payload };
            delete mainPayload.recurring;
            await ctx.root.$store.dispatch("announcement/updateAnnouncement", {
              id: announcementId.value,
              payload: mainPayload,
            });
          } else {
            // Update non-recurring announcement
            await ctx.root.$store.dispatch("announcement/updateAnnouncement", {
              id: announcementId.value,
              payload,
            });
          }
          successNotification("Announcement Updated");
        } else {
          // Create new announcement
          await ctx.root.$store.dispatch(
            "announcement/createAnnouncement",
            payload
          );
          successNotification("Announcement Created");
          setAnnouncementData();
          ctx.refs.createRef.reset();
        }

        ctx.root.$router.push("/annoucements-list");
      } catch (err) {
        const errMsg =
          err?.response?.data?.error?.error || "Something went wrong";
        errorNotification(errMsg);
      } finally {
        submitting.value = false;
      }
    };

    return {
      startDate,
      startTime,
      submitting,
      loading,
      submit,
      endDate,
      endTime,
      formData,
      clearEndDateTime,
      isEditMode,
      announcementId,
      BACKGROUND_COLORS,
      PUBLISH_OPTIONS,
      FREQUENCY_OPTIONS,
      DAYS_OF_WEEK,
      required,
    };
  },
};
</script>
